import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceAiRecommendationRecord, InsuranceApiService } from './insurance-api.service';
import {
  AI_ENTITY_TYPE_OPTIONS,
  AI_RECOMMENDATION_TYPE_OPTIONS,
  entityTypeLabelKey,
  recommendationTypeLabelKey
} from './insurance-snapshot-schemas';

@Component({
  standalone: true,
  selector: 'ain-insurance-ai-recommendations',
  imports: [
    DecimalPipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    SelectModule,
    FormsModule,
    AinDatePipe
  ],
  template: `
    <ain-page
      [title]="t('insurance.ai.recommendations.title')"
      [description]="t('insurance.ai.recommendations.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.aiRecommendations.help.page.title'"
        [descriptionKey]="'insurance.aiRecommendations.help.page.description'"
        [bulletKeys]="[
          'insurance.aiRecommendations.help.page.bullets.scope',
          'insurance.aiRecommendations.help.page.bullets.advisory',
          'insurance.aiRecommendations.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.aiRecommendations.help.listFilters.title'"
                [descriptionKey]="'insurance.aiRecommendations.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.aiRecommendations.help.listFilters.bullets.entityType',
                  'insurance.aiRecommendations.help.listFilters.bullets.recommendationType',
                  'insurance.aiRecommendations.help.listFilters.bullets.refresh'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.ai.fields.entityType') }}</span>
                <p-select
                  [options]="entityTypeOptions"
                  optionValue="value"
                  [(ngModel)]="filterEntityType"
                  (ngModelChange)="onFilterChanged()"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.ai.fields.recommendationType') }}</span>
                <p-select
                  [options]="recommendationTypeOptions"
                  optionValue="value"
                  [(ngModel)]="filterRecommendationType"
                  (ngModelChange)="onFilterChanged()"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.aiRecommendations.states.empty')" [message]="t('insurance.aiRecommendations.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.ai.fields.recommendationType') }}</th>
                  <th>{{ t('insurance.ai.fields.entityType') }}</th>
                  <th>{{ t('insurance.ai.fields.score') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('insurance.ai.fields.modelVersion') }}</th>
                  <th>{{ t('insurance.uwInbox.fields.createdAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>
                    <button type="button" class="link-btn" (click)="openDetail(row.id)">
                      {{ t(recommendationTypeLabelKey(row.recommendationType)) }}
                    </button>
                  </td>
                  <td>{{ t(entityTypeLabelKey(row.entityType)) }}</td>
                  <td>{{ (row.score * 100) | number:'1.0-1' }}%</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.modelVersion }}</td>
                  <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceAiRecommendationsComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceAiRecommendationRecord[]>([]);
  readonly selectedRows = signal<InsuranceAiRecommendationRecord[]>([]);
  readonly entityTypeOptions = AI_ENTITY_TYPE_OPTIONS;
  readonly recommendationTypeOptions = AI_RECOMMENDATION_TYPE_OPTIONS;

  filterEntityType: string | null = null;
  filterRecommendationType: string | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listAiRecommendations({
      entityType: this.filterEntityType ?? undefined,
      recommendationType: this.filterRecommendationType ?? undefined
    }).subscribe({
      next: items => {
        this.rows.set(items);
        this.error.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.ai.recommendations.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  onFilterChanged(): void {
    this.load();
  }

  onSelectionChange(selection: InsuranceAiRecommendationRecord[] | InsuranceAiRecommendationRecord | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/ai-recommendations', id]);
  }

  statusKey(status: InsuranceAiRecommendationRecord['status']): string {
    const normalized = status.charAt(0).toLowerCase() + status.slice(1);
    return `insurance.aiRecommendations.status.${normalized}`;
  }

  entityTypeLabelKey(entityType: string): string {
    return entityTypeLabelKey(entityType);
  }

  recommendationTypeLabelKey(recommendationType: string): string {
    return recommendationTypeLabelKey(recommendationType);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
